import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';

import { ClientFilterObject } from '@common/filter.decorator';

import { C } from 'src/app/@shared/constants';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

import { DataReviewModel, DataReviewSource } from 'src/app/data-review/@shared/data-review.model';
import { OrganizationModel, OrganizationSource } from 'src/app/organization/@shared/organization.model';
import { OrganizationService } from 'src/app/organization/@shared/organization.service';

export type DataReviewListStatus = 'all' | 'requested' | 'to-be-reviewed' | 'accepted' | 'denied';

@Injectable({
  providedIn: 'root',
})
export class DataReviewService extends ContentService<DataReviewModel, DataReviewSource> {
  private path = 'datareviews';
  private activeStatusSubject = new BehaviorSubject<DataReviewListStatus>('all');
  private staleDetailIds = new Set<string>();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public activeStatus$ = this.activeStatusSubject.asObservable();

  constructor(private http: HttpClient,
    private organizationService: OrganizationService) {
    super();
  }

  public setActiveStatus(status: DataReviewListStatus): void {
    this.activeStatusSubject.next(status);
  }

  public loadContent(id: string, options: ContentOptions<DataReviewSource> = {}): Observable<DataReviewModel[] | null> {
    if (!options.type) {
      console.warn('[DataReviewService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    return this.loadDetailContent(id, options).pipe(map((item: DataReviewModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public loadDataReviewOrganizationByToken(dataReviewToken: string): Promise<OrganizationModel> {
    const url = ProtoUrl.getUrl(`${this.path}/organization/${dataReviewToken}`);

    return lastValueFrom(this.http.get<OrganizationSource>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new OrganizationModel(item)
            : null;
        })));
  }

  public patch(id: string, attributes: Partial<DataReviewSource>): Promise<DataReviewModel> {
    return this.http.patch<DataReviewSource>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: DataReviewSource) => { return new DataReviewModel(item); }),
        tap((item: DataReviewModel) => {
          this.syncOrganizationLatestDataReviewSummary(item);
          this.updateItem(item);

          if (item.status === 'accepted' || item.status === 'denied') {
            this.updateItem(item); // update (detail)
            this.updateItem(item, 'all'); // update
            this.removeItem(id, 'to-be-reviewed'); // update (also updates count indicator in main menu)
            this.updateItem(item, item.status, true); // add
          }
        }))
      .toPromise();
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<DataReviewSource>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.removeItem(id);
      }))
      .toPromise();
  }

  public removeByOrganizationId(organizationId: string): void {
    for (const storeId of Object.keys(this.itemsStore)) {
      if (this.itemsStore[storeId].find((item) => { return item.organizationId === organizationId; })) {
        this.itemsStore[storeId] = this.itemsStore[storeId]
          .filter((item) => { return item.organizationId !== organizationId; });
        this.items[storeId].next(this.itemsStore[storeId]);
      }
    }
  }

  public requestDataReview(attributes: Partial<DataReviewSource>): Promise<DataReviewModel> {
    return lastValueFrom(this.http.post<DataReviewSource>(ProtoUrl.requestDatareview(), attributes)
      .pipe(map((item: DataReviewSource) => { return new DataReviewModel(item); }),
        tap((item: DataReviewModel) => {
          this.syncOrganizationLatestDataReviewSummary(item);
          // we need to add our item to 'all' and 'requested' store
          this.updateItem(item, 'all', true);
          this.updateItem(item, 'requested', true);
        })));
  }

  public updateDatareviewsChangedOrganization(changedOrganization: Partial<OrganizationSource>, token: string): Promise<void> {
    const attributes = {
      changedOrganization,
      token,
    };

    return lastValueFrom(this.http.post<void>(ProtoUrl.updateDatareviewsChangedOrganization(), attributes));
  }

  public isDetailStale(id: string): boolean {
    return this.staleDetailIds.has(id);
  }

  protected loadListContent(id: string, options: ContentOptions<DataReviewSource>): Observable<DataReviewModel[]> {
    const filter: ClientFilterObject<DataReviewSource> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[DataReviewService] load items', url);
    let totalCount: number;

    return this.http.get<DataReviewSource[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<DataReviewSource[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<DataReviewSource[]>) => { return response.body || []; }),
        map((items: DataReviewSource[]) => { return items.map((item) => { return new DataReviewModel(item); }); }),
        tap((items: DataReviewModel[]) => {
          this.setItems(items, id, filter, totalCount);

          // Mark cached detail entries as stale when list metadata changed.
          for (const item of items) {
            if (!this.items[item.id] || !this.itemsStore[item.id]?.length) {
              continue;
            }

            const [cachedDetail] = this.itemsStore[item.id];

            if (!cachedDetail) {
              continue;
            }

            if (cachedDetail.updatedAt !== item.updatedAt || cachedDetail.status !== item.status) {
              this.staleDetailIds.add(item.id);
            }
          }
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<DataReviewSource>): Observable<DataReviewModel> {
    const { filter } = options;
    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<DataReviewSource>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new DataReviewModel(item)
            : null;
        }),
        tap((item) => {
          this.updateItem(item);

          if (item?.id) {
            this.staleDetailIds.delete(item.id);
          }
        }));
  }

  private syncOrganizationLatestDataReviewSummary(dataReview: DataReviewModel): void {
    if (!dataReview?.organizationId) {
      return;
    }

    if (dataReview.status === 'denied') {
      this.organizationService.updateLatestDataReviewSummary(dataReview.organizationId, null);

      return;
    }

    this.organizationService.updateLatestDataReviewSummary(dataReview.organizationId, {
      createdAt: dataReview.createdAt,
      id: dataReview.id,
      notes: dataReview.notes,
      status: dataReview.status,
    });
  }
}
